import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const WhatsAppAlertsCard = () => {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetch(`${API}/owner/whatsapp/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/owner/whatsapp/test`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "fail");
      toast.success(t("wa_test_ok"));
    } catch (e) {
      toast.error(`${t("wa_test_fail")} ${e.message || ""}`.trim());
    } finally {
      setSending(false);
    }
  };

  const ok = !!status?.configured;

  return (
    <section
      className="mb-14 rounded-3xl bg-[#132338] border border-[#203859] p-6 flex flex-col md:flex-row md:items-center gap-5"
      data-testid="wa-alerts-card"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#25D366]">
        <MessageCircle className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg font-bold text-white">{t("wa_alerts_title")}</h2>
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] font-bold ${
              ok ? "bg-[#25D366]/15 text-[#25D366]" : "bg-amber-500/15 text-amber-400"
            }`}
            data-testid="wa-alerts-status"
          >
            {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {ok ? (status.sandbox ? t("wa_status_sandbox") : t("wa_status_on")) : t("wa_status_off")}
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-400">
          {ok ? `${t("wa_alerts_on_sub")} ${status.to?.replace("whatsapp:", "")}` : t("wa_alerts_off_sub")}
        </p>
        {ok && status.sandbox && (
          <p className="mt-1 text-xs text-amber-400/90">{t("wa_sandbox_hint")}</p>
        )}
      </div>
      {ok && (
        <button
          onClick={sendTest}
          disabled={sending}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white font-display font-bold px-5 py-3 text-sm transition-[background-color,transform] duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60 whitespace-nowrap"
          data-testid="wa-send-test-btn"
        >
          <Send className="h-4 w-4" /> {sending ? t("wa_sending") : t("wa_send_test")}
        </button>
      )}
    </section>
  );
};
